import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { NEEDS, PHONE, PHONE_HREF } from "../../data/site";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { nome: "", telefono: "", email: "", bisogno: "", messaggio: "", privacy: false, website: "" };

const Field = ({ label, children, htmlFor }) => (
  <label htmlFor={htmlFor} className="group block">
    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate2">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-white px-4 py-3.5 text-base text-navy outline-none transition-[border-color,box-shadow] placeholder:text-slate2/60 focus:border-navy focus:shadow-[0_0_0_4px_rgba(198,173,118,0.25)]";

export const ContactForm = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    const tipo = params.get("tipo");
    if (tipo && NEEDS.some((n) => n.value === tipo)) setForm((f) => ({ ...f, bisogno: tipo }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.privacy) {
      setError("Per inviare la richiesta è necessario prendere visione dell'informativa privacy.");
      return;
    }
    if (!API) {
      setError(`Il modulo non è al momento disponibile. Puoi contattarci al ${PHONE}.`);
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/richieste`, form);
      setStatus("done");
      setForm(EMPTY);
    } catch (err) {
      setStatus("idle");
      const s = err?.response?.status;
      setError(
        s === 422
          ? "Controlla i campi inseriti: alcuni dati non sono validi."
          : s === 429
          ? "Hai inviato diverse richieste in poco tempo. Riprova tra qualche minuto."
          : `Non è stato possibile inviare la richiesta. Riprova oppure chiama la Centrale Operativa al ${PHONE}.`
      );
    }
  };

  return (
    <div className="relative" data-testid="contact-form-wrapper">
      <AnimatePresence initial={false}>
        {status === "done" ? (
          <motion.div
            key="ok"
            data-testid="contact-form-success"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-start rounded-2xl border border-gold/40 bg-white p-8 md:p-10"
          >
            <CheckCircle2 className="h-10 w-10 text-gold" strokeWidth={1.3} />
            <h3 className="mt-5 text-2xl font-semibold text-navy">Richiesta inviata. Grazie.</h3>
            <p className="mt-3 max-w-md text-base leading-relaxed text-slate2">
              Abbiamo ricevuto la tua richiesta: la Centrale Operativa la prenderà in carico e ti ricontatterà ai recapiti indicati.
              Per esigenze di servizio puoi chiamarci al{" "}
              <a href={PHONE_HREF} className="font-semibold text-navy underline underline-offset-2">{PHONE}</a>.
            </p>
            <button type="button" onClick={() => setStatus("idle")} data-testid="contact-form-new-btn" className="mt-6 text-sm font-semibold text-navy underline underline-offset-4 hover:text-gold-ink">
              Invia un'altra richiesta
            </button>
          </motion.div>
        ) : (
          <motion.form key="form" onSubmit={submit} data-testid="contact-form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid gap-6 md:grid-cols-2">
            <Field label="Nome e cognome" htmlFor="cf-nome">
              <input id="cf-nome" required minLength={2} value={form.nome} onChange={set("nome")} className={inputCls} autoComplete="name" data-testid="contact-form-name-input" />
            </Field>
            <Field label="Telefono" htmlFor="cf-tel">
              <input id="cf-tel" required type="tel" minLength={5} value={form.telefono} onChange={set("telefono")} className={inputCls} autoComplete="tel" data-testid="contact-form-phone-input" />
            </Field>
            <Field label="Email" htmlFor="cf-email">
              <input id="cf-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} autoComplete="email" data-testid="contact-form-email-input" />
            </Field>
            <Field label="Di cosa hai bisogno" htmlFor="cf-need">
              <select id="cf-need" required value={form.bisogno} onChange={set("bisogno")} className={`${inputCls} select-chevron appearance-none pr-10`} data-testid="contact-form-need-select">
                <option value="" disabled>Seleziona un'opzione</option>
                {NEEDS.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Messaggio" htmlFor="cf-msg">
                <textarea id="cf-msg" required minLength={5} rows={5} value={form.messaggio} onChange={set("messaggio")} placeholder="Descrivi la tua esigenza: luoghi, date, eventuali necessità particolari." className={`${inputCls} resize-y`} data-testid="contact-form-message-input" />
              </Field>
            </div>
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            <label className="flex items-start gap-3 text-sm text-slate2 md:col-span-2">
              <input type="checkbox" checked={form.privacy} onChange={set("privacy")} className="mt-1 h-4 w-4 accent-[#18314F]" data-testid="contact-form-privacy-checkbox" />
              <span>
                Ho letto l'<Link to="/privacy-policy" className="font-semibold text-navy underline underline-offset-2">informativa privacy</Link> e acconsento al trattamento dei dati per la gestione della mia richiesta.
              </span>
            </label>
            {error && <p role="alert" data-testid="contact-form-error" className="rounded-xl bg-rosso/5 px-4 py-3 text-sm text-rosso md:col-span-2">{error}</p>}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center md:col-span-2">
              <button type="submit" disabled={status === "loading"} data-testid="contact-form-submit-btn" className="inline-flex items-center justify-center gap-3 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-[background-color,transform] hover:bg-navy-deep active:scale-[0.98] disabled:opacity-70">
                {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Invia la richiesta
              </button>
              <p className="text-xs text-slate2">Per le emergenze non usare questo modulo: chiama il 112.</p>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
